/**
 * Exit Intent Modal — Trial Conversion
 * Triggers when user moves mouse to close the tab (desktop only)
 * Shows a compelling trial offer to reduce bounce
 */
import { useState, useEffect, useCallback } from 'react';
import { X, Clock, Shield, BarChart3, ArrowRight } from 'lucide-react';
import { trackExitIntent, trackTrialClick } from '@/lib/analytics';
import { Link } from 'react-router';

const STORAGE_KEY = 'exit_intent_dismissed';
const COOLDOWN_HOURS = 24;

function getDismissedTime(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseInt(raw, 10) : null;
  } catch {
    return null;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  } catch { /* ignore */ }
}

function isCooldownActive(): boolean {
  const dismissed = getDismissedTime();
  if (!dismissed) return false;
  const hoursSince = (Date.now() - dismissed) / (1000 * 60 * 60);
  return hoursSince < COOLDOWN_HOURS;
}

export default function ExitIntentModal() {
  const [visible, setVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    // Only trigger if mouse leaves through the top of the page
    if (e.clientY <= 10 && !hasTriggered && !isCooldownActive()) {
      setHasTriggered(true);
      setVisible(true);
      trackExitIntent('shown');
    }
  }, [hasTriggered]);

  useEffect(() => {
    // Only on desktop — mobile doesn't have mouse leave
    if (typeof window === 'undefined' || 'ontouchstart' in window) return;

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [handleMouseLeave]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed();
    trackExitIntent('dismissed');
  };

  const handleConvert = () => {
    setVisible(false);
    setDismissed();
    trackExitIntent('converted');
    trackTrialClick('exit_intent');
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(5,7,11,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={handleDismiss}
      role="dialog"
      aria-modal="true"
      aria-label="Free trial offer"
    >
      <div
        className="relative w-full max-w-lg rounded-2xl p-8 shadow-2xl"
        style={{
          background: 'linear-gradient(145deg, #0A0D14 0%, #111827 100%)',
          border: '1px solid rgba(199,255,47,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-4 rounded-lg p-1 transition-colors hover:bg-white/5"
          aria-label="Close offer"
        >
          <X size={20} style={{ color: '#8A9BAE' }} />
        </button>

        {/* Badge */}
        <div
          className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider"
          style={{ background: 'rgba(199,255,47,0.1)', color: '#C7FF2F', border: '1px solid rgba(199,255,47,0.2)' }}
        >
          <Clock size={12} />
          Wait — Special Offer
        </div>

        {/* Headline */}
        <h2 className="mb-3 text-2xl font-extrabold" style={{ color: '#F8FAFC', letterSpacing: '-0.5px' }}>
          Start Your 7-Day Free Trial
        </h2>

        {/* Subheadline */}
        <p className="mb-6 text-sm leading-relaxed" style={{ color: 'rgba(245,247,250,0.6)' }}>
          Use Parcel Lead Pro to find undervalued parcels before the competition. AI-powered land investment intelligence for serious investors.
        </p>

        {/* Value props */}
        <div className="mb-6 grid grid-cols-2 gap-3">
          {[
            { icon: <BarChart3 size={16} />, text: 'AI parcel scoring' },
            { icon: <Shield size={16} />, text: 'Risk analysis' },
            { icon: <Clock size={16} />, text: 'Weekly data updates' },
            { icon: <ArrowRight size={16} />, text: 'South Carolina coverage' },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-xs font-semibold"
              style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,247,250,0.7)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <span style={{ color: '#C7FF2F' }}>{item.icon}</span>
              {item.text}
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          to="/register"
          onClick={handleConvert}
          className="mb-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold transition-all hover:translate-y-[-1px]"
          style={{ background: '#7CFF6B', color: '#07110D' }}
        >
          Start My 7-Day Free Trial
          <ArrowRight size={16} />
        </Link>

        {/* Dismiss text */}
        <button
          onClick={handleDismiss}
          className="w-full text-center text-xs font-medium transition-colors hover:text-[#F7F8F4]/60"
          style={{ color: 'rgba(138,155,174,0.6)' }}
        >
          No thanks, I prefer to research manually
        </button>
      </div>
    </div>
  );
}
